import {
  addDoc as addDocSDK,
  collection as collectionSDK,
  collectionGroup as collectionGroupSDK,
  deleteDoc as deleteDocSDK,
  doc as docSDK,
  endAt as endAtSDK,
  getDoc as getDocSDK,
  getDocs as getDocsSDK,
  limit as limitSDK,
  onSnapshot as onSnapshotSDK,
  orderBy as orderBySDK,
  query as querySDK,
  startAfter as startAfterSDK,
  startAt as startAtSDK,
  serverTimestamp as serverTimestampSDK,
  setDoc as setDocSDK,
  updateDoc as updateDocSDK,
  where as whereSDK,
  Timestamp as TimestampSDK,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type Firestore,
  type FirestoreError,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  type WhereFilterOp,
  type Unsubscribe,
  arrayUnion as arrayUnionSDK,
  arrayRemove as arrayRemoveSDK
} from 'firebase/firestore';
import { db } from './firebase';
import {
  getTestOverrides,
  type EmployeeRecord,
  type LeaveAttachmentMetadata,
  type LeaveRequestRecord,
  type LeaveRequestWrite
} from './testOverrides';
import type { HolidayCalendar, LeavePolicy } from './types/leave';
import { debouncedSafeOnSnapshot } from './safeFirestore';
import { Department, Team, JobTitle } from './types/organization';
import { normalizeString } from '../utils/normalize';
import { PAGINATION_LIMITS } from './constants';

type EmployeeRecordWithIdentifiers = EmployeeRecord & {
  employeeNumber?: string | null;
  externalEmployeeId?: string | null;
  departmentId?: string | null;
  teamId?: string | null;
  holidayCalendarId?: string | null;
  leavePolicyAssignments?: Record<string, boolean> | null;
  hireDate?: string | null;
  // Avatar fields for profile display
  photoURL?: string | null;
  initials?: string | null;
  avatarColor?: string | null;
};

function mapEmployeeRecord(id: string, data: DocumentData): EmployeeRecordWithIdentifiers {
  const fullName =
    normalizeString(data.fullName) ||
    normalizeString(data.displayName) ||
    normalizeString(data.name);
  const email = normalizeString(data.email) ?? null;
  const managerUid = normalizeString(data.managerUid) || normalizeString(data.managerId);

  const record: EmployeeRecordWithIdentifiers = {
    id,
    uid: id,
    fullName,
    title: normalizeString(data.title),
    department: normalizeString(data.department),
    managerUid,
    managerName: normalizeString(data.managerName),
    workPhone: normalizeString(data.workPhone),
    city: normalizeString(data.city),
    country: normalizeString(data.country),
    employmentType: normalizeString(data.employmentType) as EmployeeRecord['employmentType'],
    workArrangement: normalizeString(data.workArrangement) as EmployeeRecord['workArrangement'],
    salaryUSD: typeof data.salaryUSD === 'number' ? data.salaryUSD : undefined,
    taxNumber: normalizeString(data.taxNumber),
    dob: normalizeString(data.dob),
    address: normalizeString(data.address),
    personalEmail: normalizeString(data.personalEmail),
    personalPhone: normalizeString(data.personalPhone),
    nationality: normalizeString(data.nationality),
    name: fullName ?? null,
    email,
    role: normalizeString(data.role) ?? null,
    authUid: normalizeString(data.authUid) ?? null,
    status: (normalizeString(data.status) || 'pending') as EmployeeRecord['status'],
    activatedAt: normalizeString(data.activatedAt) ?? null,
    deactivatedAt: normalizeString(data.deactivatedAt) ?? null,
    // Avatar fields for profile display
    photoURL: normalizeString(data.photoURL) ?? null,
    initials: normalizeString(data.initials) ?? null,
    avatarColor: normalizeString(data.avatarColor) ?? null,
  };

  const employeeNumber = normalizeString(data.employeeNumber);
  if (employeeNumber) {
    record.employeeNumber = employeeNumber;
  }

  const externalEmployeeId = normalizeString(data.externalEmployeeId);
  if (externalEmployeeId) {
    record.externalEmployeeId = externalEmployeeId;
  }

  // Department and Team IDs for organization structure
  const departmentId = normalizeString(data.departmentId);
  if (departmentId) {
    record.departmentId = departmentId;
  }

  const teamId = normalizeString(data.teamId);
  if (teamId) {
    record.teamId = teamId;
  }

  // Leave-related fields
  const holidayCalendarId = normalizeString(data.holidayCalendarId);
  if (holidayCalendarId) {
    record.holidayCalendarId = holidayCalendarId;
  }

  // Leave policy assignments (Record<string, boolean>)
  if (data.leavePolicyAssignments && typeof data.leavePolicyAssignments === 'object') {
    record.leavePolicyAssignments = data.leavePolicyAssignments as Record<string, boolean>;
  }

  // Hire date
  const hireDate = normalizeString(data.hireDate);
  if (hireDate) {
    record.hireDate = hireDate;
  }

  return record;
}

export function toManagerRef(
  managerUid: string | null | undefined,
  employees: Pick<EmployeeRecord, 'id' | 'uid' | 'fullName' | 'name'>[]
): { managerUid: string | null; managerName: string | null } {
  if (typeof managerUid !== 'string') {
    return { managerUid: null, managerName: null };
  }

  const trimmed = managerUid.trim();

  if (!trimmed) {
    return { managerUid: null, managerName: null };
  }

  const match = employees.find((employee) => {
    const uid = employee.uid ?? employee.id;
    return uid === trimmed;
  });

  const managerName = match?.fullName ?? match?.name ?? null;

  return {
    managerUid: trimmed,
    managerName
  };
}

function mapDirectoryEntry(
  id: string,
  data: DocumentData
): Pick<
  EmployeeRecord,
  'uid' | 'fullName' | 'title' | 'department' | 'managerName' | 'email' | 'workPhone' | 'city' | 'country' | 'workArrangement' | 'dob'
> {
  const fullName =
    normalizeString(data.fullName) ||
    normalizeString(data.displayName) ||
    normalizeString(data.name);

  const entry: Pick<
    EmployeeRecord,
    'uid' | 'fullName' | 'title' | 'department' | 'managerName' | 'email' | 'workPhone' | 'city' | 'country' | 'workArrangement' | 'dob'
  > = {
    uid: id
  };

  if (fullName) {
    entry.fullName = fullName;
  }

  const title = normalizeString(data.title);
  if (title) {
    entry.title = title;
  }

  const department = normalizeString(data.department) ?? normalizeString(data.departmentName);
  if (department) {
    entry.department = department;
  }

  const managerName = normalizeString(data.managerName);
  if (managerName) {
    entry.managerName = managerName;
  }

  const email = normalizeString(data.email);
  if (email) {
    entry.email = email;
  }

  const workPhone = normalizeString(data.workPhone);
  if (workPhone) {
    entry.workPhone = workPhone;
  }

  const city = normalizeString(data.city) ?? normalizeString(data.location);
  if (city) {
    entry.city = city;
  }

  const country = normalizeString(data.country);
  if (country) {
    entry.country = country;
  }

  const workArrangement = normalizeString(data.workArrangement) as EmployeeRecord['workArrangement'];
  if (workArrangement) {
    entry.workArrangement = workArrangement;
  }

  const dob = normalizeString(data.dob);
  if (dob) {
    entry.dob = dob;
  }

  return entry;
}

export async function fetchEmployees(tenantId: string): Promise<EmployeeRecordWithIdentifiers[]> {
  const overrides = getTestOverrides();
  if (overrides?.employees) {
    return overrides.employees.map((employee) => ({
      ...employee,
      id: employee.id,
      uid: employee.uid ?? employee.id
    }));
  }

  const employeesCol = collectionSDK(db, 'tenants', tenantId, 'employees');
  const snapshot = await getDocsSDK(employeesCol);

  return snapshot.docs.map((docSnap) => mapEmployeeRecord(docSnap.id, docSnap.data() as DocumentData));
}

export async function getEmployeeDoc(
  tenantId: string,
  uid: string
): Promise<EmployeeRecordWithIdentifiers | null> {
  const overrides = getTestOverrides();
  if (overrides?.employees) {
    const match = overrides.employees.find((employee) => employee.uid === uid || employee.id === uid);
    if (!match) {
      return null;
    }

    return {
      ...match,
      id: match.id ?? uid,
      uid: match.uid ?? uid
    };
  }

  const employeeDoc = docSDK(db, 'tenants', tenantId, 'employees', uid);
  const snapshot = await getDocSDK(employeeDoc);

  if (!snapshot.exists()) {
    return null;
  }

  return mapEmployeeRecord(snapshot.id, snapshot.data() as DocumentData);
}

export async function getDirectoryEntry(
  tenantId: string,
  uid: string
): Promise<
  Pick<
    EmployeeRecord,
    'uid' | 'fullName' | 'title' | 'department' | 'managerName' | 'email' | 'workPhone' | 'city' | 'country' | 'workArrangement' | 'dob'
  > | null
> {
  const directoryDoc = docSDK(db, 'tenants', tenantId, 'directory', uid);
  const snapshot = await getDocSDK(directoryDoc);

  if (!snapshot.exists()) {
    return null;
  }

  return mapDirectoryEntry(snapshot.id, snapshot.data() as DocumentData);
}

export async function fetchLeavePolicy(tenantId: string): Promise<LeavePolicy | null> {
  const overrides = getTestOverrides();
  if (overrides && 'leavePolicy' in overrides) {
    return overrides.leavePolicy ?? null;
  }

  const policyDoc = docSDK(db, 'tenants', tenantId, 'policy', 'leave');
  const snapshot = await getDocSDK(policyDoc);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as LeavePolicy;
}

/**
 * Map a leave request document to LeaveRequestRecord
 */
function mapLeaveRequestDoc(docSnap: { id: string; data: () => DocumentData }): LeaveRequestRecord {
  const data = docSnap.data() as DocumentData;
  return {
    id: docSnap.id,
    type: (data.type as string) ?? 'unknown',
    leavePolicyId: (data.leavePolicyId as string | undefined) ?? undefined,
    leavePolicyName: (data.leavePolicyName as string | undefined) ?? undefined,
    startDate: (data.startDate as string) ?? '',
    endDate: (data.endDate as string) ?? '',
    days: Number(data.days) || 0,
    status: (data.status as string) ?? 'pending',
    reason: (data.reason as string | undefined) ?? null,
    documentRequired: Boolean(data.documentRequired),
    documentAttached: Boolean(data.documentAttached),
    attachment: (data.attachment as LeaveAttachmentMetadata | null | undefined) ?? undefined,
    employeeUid: (data.employeeUid as string | undefined) ?? undefined,
    employeeId: (data.employeeId as string | undefined) ?? undefined,
    createdBy: (data.createdBy as string | undefined) ?? undefined,
  } satisfies LeaveRequestRecord;
}

/**
 * Fetch leave requests for current user only (used as fallback for non-admin users)
 * Queries by employeeUid to match what the backend writes (buildLeaveRequestData)
 * and the Firestore rules isRequestOwner() check
 */
async function fetchUserLeaveRequests(tenantId: string, userId: string): Promise<LeaveRequestRecord[]> {
  const requestsCol = collectionSDK(db, 'tenants', tenantId, 'leaveRequests');
  // Query by employeeUid — this is what the backend writes in buildLeaveRequestData()
  // Firestore rules isRequestOwner() also checks employeeUid == request.auth.uid
  const requestsQuery = querySDK(
    requestsCol,
    whereSDK('employeeUid', '==', userId),
    orderBySDK('createdAt', 'desc')
  );
  const snapshot = await getDocsSDK(requestsQuery);
  return snapshot.docs.map(mapLeaveRequestDoc);
}

/**
 * Fetch leave requests for a tenant
 *
 * SECURITY: For regular employees (non-admin/manager), this function will fail on the
 * initial query (fetching all requests) due to Firestore rules. In that case, we fall back
 * to fetching only the user's own requests using the userId filter.
 *
 * @param tenantId - The tenant ID
 * @param userId - Optional user ID for fallback query (required for non-admin users)
 * @param forceUserOnly - If true, skips the "try all" step and immediately fetches user requests
 */
export async function fetchLeaveRequests(
  tenantId: string,
  userId?: string,
  forceUserOnly: boolean = false
): Promise<LeaveRequestRecord[]> {
  const overrides = getTestOverrides();
  if (overrides?.leaveRequests) {
    return overrides.leaveRequests;
  }

  if (forceUserOnly && userId) {
    return fetchUserLeaveRequests(tenantId, userId);
  }

  const requestsCol = collectionSDK(db, 'tenants', tenantId, 'leaveRequests');
  // Add limit for basic scalability safety (Post-MVP: Implement real pagination)
  const requestsQuery = querySDK(
    requestsCol,
    orderBySDK('createdAt', 'desc'),
    limitSDK(PAGINATION_LIMITS.timeOffRequests)
  );

  try {
    // First try to fetch all requests (requires admin/manager role)
    const snapshot = await getDocsSDK(requestsQuery);
    return snapshot.docs.map(mapLeaveRequestDoc);
  } catch (error) {
    // Check if this is a permission denied error
    const errorMessage = error instanceof Error ? error.message : String(error);
    const isPermissionDenied =
      errorMessage.includes('permission-denied') ||
      errorMessage.includes('Missing or insufficient permissions') ||
      (error && typeof error === 'object' && 'code' in error && error.code === 'permission-denied');

    if (isPermissionDenied && userId) {
      // Fall back to fetching only user's own leave requests
      console.info('[fetchLeaveRequests] Permission denied, fetching user\'s own requests');
      return fetchUserLeaveRequests(tenantId, userId);
    }

    // Re-throw other errors
    throw error;
  }
}

export async function createLeaveRequest(
  tenantId: string,
  payload: Omit<LeaveRequestWrite, 'createdAt'>
): Promise<string> {
  const overrides = getTestOverrides();
  const requestData: LeaveRequestWrite = {
    ...payload,
    createdAt: serverTimestampSDK()
  };

  if (overrides?.onCreateLeaveRequest) {
    const result = await overrides.onCreateLeaveRequest(requestData);
    if (result && typeof result === 'object' && 'id' in result && result.id) {
      return result.id;
    }
    return `test-request-${Date.now()}`;
  }

  const requestsCol = collectionSDK(db, 'tenants', tenantId, 'leaveRequests');
  const requestRef = docSDK(requestsCol);
  await setDocSDK(requestRef, requestData);
  return requestRef.id;
}

export async function markLeaveRequestDocumentAttached(
  tenantId: string,
  requestId: string,
  attachment?: LeaveAttachmentMetadata
) {
  const overrides = getTestOverrides();
  if (overrides?.leaveRequests) {
    overrides.leaveRequests = overrides.leaveRequests.map((request) =>
      request.id === requestId
        ? { ...request, documentAttached: true, attachment: attachment ?? request.attachment }
        : request
    );
    return;
  }

  const requestDoc = docSDK(db, 'tenants', tenantId, 'leaveRequests', requestId);
  await updateDocSDK(requestDoc, {
    documentAttached: true,
    ...(attachment ? { attachment } : {})
  });
}

export type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Firestore,
  FirestoreError,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
  Unsubscribe
};

// Export Timestamp class (which includes both the type and the constructor)
// This allows: const t: Timestamp = Timestamp.now()
export { TimestampSDK as Timestamp };

export const serverTimestamp = serverTimestampSDK;
export const getDoc = getDocSDK;
export const getDocs = getDocsSDK;
export const setDoc = setDocSDK;
export const addDoc = addDocSDK;
export const updateDoc = updateDocSDK;
export const deleteDoc = deleteDocSDK;
export const query = querySDK;
export const collection = collectionSDK;
export const doc = docSDK;
export const arrayUnion = arrayUnionSDK;
export const arrayRemove = arrayRemoveSDK;
export { db };

export function docRef(
  first: Firestore | CollectionReference<DocumentData> | string,
  ...segments: string[]
): DocumentReference<DocumentData> {
  if (typeof first === 'string') {
    return docSDK(db, first, ...segments);
  }

  if ('id' in first && 'path' in first) {
    // It's a CollectionReference
    return docSDK(first as CollectionReference<DocumentData>, ...segments);
  }

  // It's a Firestore instance - construct path from segments
  if (segments.length === 0) {
    throw new Error('docRef: segments required when first argument is Firestore instance');
  }
  const pathString = segments.join('/');
  return docSDK(first as Firestore, pathString);
}

export function collectionRef(
  first: Firestore | DocumentReference<DocumentData> | string,
  ...segments: string[]
): CollectionReference<DocumentData> {
  if (typeof first === 'string') {
    return collectionSDK(db, first, ...segments) as CollectionReference<DocumentData>;
  }

  if ('id' in first && 'path' in first && 'parent' in first) {
    // It's a DocumentReference
    if (segments.length === 0) {
      throw new Error('collectionRef: segments required when first argument is DocumentReference');
    }
    // Construct path from segments and use single string overload
    const pathString = segments.join('/');
    return collectionSDK(first as DocumentReference<DocumentData>, pathString) as CollectionReference<DocumentData>;
  }

  // It's a Firestore instance - construct path from segments
  if (segments.length === 0) {
    throw new Error('collectionRef: segments required when first argument is Firestore instance');
  }
  const pathString = segments.join('/');
  return collectionSDK(first as Firestore, pathString) as CollectionReference<DocumentData>;
}

export function tenantCollectionRef(tenantId: string, collectionName: string) {
  return collectionRef('tenants', tenantId, collectionName);
}

export function tenantDocRef(tenantId: string, collectionName: string, docId: string) {
  return docRef('tenants', tenantId, collectionName, docId);
}

export function queryWithConstraints<T = DocumentData>(
  source: Query<T> | CollectionReference<T>,
  constraints: QueryConstraint[]
): Query<T> {
  if (constraints.length === 0) {
    return source as Query<T>;
  }

  // Firestore query() accepts constraints in any order, but TypeScript overloads are strict
  // Cast through a function type that accepts a rest spread to work around overload matching limitations
  return (querySDK as (q: Parameters<typeof querySDK>[0], ...c: QueryConstraint[]) => Query<T>)(
    source as unknown as Parameters<typeof querySDK>[0], ...constraints
  );
}

export const whereEqualTo = (field: string, value: unknown) => whereSDK(field, '==', value);

export const whereField = (
  field: string,
  op: WhereFilterOp,
  value: unknown
) => whereSDK(field, op, value);

export const where = whereField;

export const orderByField = (field: string, direction: 'asc' | 'desc' = 'asc') =>
  orderBySDK(field, direction);

export const orderBy = orderByField;

export const limit = (value: number) => limitSDK(value);

export const startAt = (...values: unknown[]) => startAtSDK(...values);

export const startAfter = (...values: unknown[]) => startAfterSDK(...values);

export const endAt = (...values: unknown[]) => endAtSDK(...values);

export const queryTenantCollection = (
  tenantId: string,
  collectionName: string,
  constraints: QueryConstraint[]
) => queryWithConstraints(tenantCollectionRef(tenantId, collectionName), constraints);

/**
 * Safe onSnapshot wrapper that suppresses Firestore internal assertion errors
 * during component unmount/navigation. This is a known Firebase SDK bug.
 * @see https://github.com/firebase/firebase-js-sdk/issues/8052
 */
function createSafeUnsubscribe(unsubscribe: Unsubscribe): Unsubscribe {
  return () => {
    try {
      unsubscribe();
    } catch (error) {
      // Silently ignore Firestore internal assertion errors during cleanup
      if (error instanceof Error && error.message.includes('INTERNAL ASSERTION FAILED')) {
        console.debug('[firestoreClient] Suppressed Firestore cleanup error (safe to ignore)');
      } else {
        console.warn('[firestoreClient] Unexpected error during unsubscribe:', error);
      }
    }
  };
}

// CRITICAL FIX: Apply 200ms debounce to onSnapshot to prevent React StrictMode race conditions
// StrictMode double-mounts components in development, causing overlapping onSnapshot listeners
// that corrupt the Firestore SDK's internal WatchStream state machine (ca9/b815 errors)
export const onSnapshot: typeof onSnapshotSDK = ((...args: Parameters<typeof onSnapshotSDK>) => {
  let unsubscribe: Unsubscribe | null = null;
  let cancelled = false;

  // Debounce the listener creation to let StrictMode's first unmount cycle complete
  const timerId = setTimeout(() => {
    if (cancelled) return;
    unsubscribe = onSnapshotSDK(...args);
  }, 200);

  // Return cleanup function that cancels timer and unsubscribes
  return createSafeUnsubscribe(() => {
    cancelled = true;
    clearTimeout(timerId);
    if (unsubscribe) {
      unsubscribe();
    }
  });
}) as typeof onSnapshotSDK;

export const listenCollection = (
  tenantId: string,
  collectionName: string,
  constraints: QueryConstraint[],
  onNext: (snapshot: QuerySnapshot<DocumentData>) => void,
  onError: (error: FirestoreError) => void
): Unsubscribe => {
  const base = tenantCollectionRef(tenantId, collectionName);
  const queryRef = queryWithConstraints(base, constraints);
  // CRITICAL FIX: Use debounced wrapper to prevent React StrictMode race condition
  // StrictMode double-mounts components, causing overlapping onSnapshot listeners
  // that corrupt the Firestore SDK's internal WatchStream state machine (ca9/b815 errors)
  return debouncedSafeOnSnapshot(
    queryRef,
    onNext,
    (error) => {
      // Suppress internal assertion errors - these occur during StrictMode cleanup
      if (error.message?.includes('INTERNAL ASSERTION FAILED')) {
        console.debug('[firestoreClient] Suppressed Firestore internal assertion error');
        return;
      }
      // Suppress AbortError - expected when listeners are cancelled during unmount
      // This commonly occurs with React StrictMode double-mounting or fast navigation
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.debug('[firestoreClient] Suppressed AbortError during listener cleanup');
        return;
      }
      onError(error);
    }
  );
};

export const getDocSnapshot = (reference: DocumentReference<DocumentData>) =>
  getDocSDK(reference);

export const getTenantDoc = (tenantId: string, collectionName: string, docId: string) =>
  getDocSnapshot(tenantDocRef(tenantId, collectionName, docId));

export const updateDocSafe = (
  reference: DocumentReference<DocumentData>,
  data: Record<string, unknown>
) => updateDocSDK(reference, data);

export const addDocToCollection = (
  reference: CollectionReference<DocumentData>,
  data: Record<string, unknown>
) => addDocSDK(reference, data);

export const setFileVisible = (
  tenantId: string,
  collectionName: string,
  docId: string,
  visible: boolean
) => {
  const reference = tenantDocRef(tenantId, collectionName, docId);
  return updateDocSDK(reference, { visible });
};

export const collectionGroupRef = <T = DocumentData>(
  dbInstance: Firestore,
  collectionId: string
) => collectionGroupSDK(dbInstance, collectionId) as Query<T>;

// --- Holiday Calendar Management ---

/**
 * Creates a new Holiday Calendar document for the tenant.
 * @param tenantId The tenant's ID.
 * @param name The display name for the calendar (e.g., "UK - England").
 * @param holidays The initial map of holidays (Record<string, string>).
 */
export async function createHolidayCalendar(
  tenantId: string,
  name: string,
  holidays: Record<string, string>
): Promise<HolidayCalendar> {
  const newDocRef = docSDK(collectionSDK(db, 'tenants', tenantId, 'holidayCalendars'));

  const newCalendar: HolidayCalendar = {
    id: newDocRef.id,
    tenantId,
    name,
    holidays,
  };

  await setDocSDK(newDocRef, newCalendar);
  return newCalendar;
}

/**
 * Updates an existing Holiday Calendar document.
 * @param tenantId The tenant's ID.
 * @param calendarId The ID of the calendar to update.
 * @param updates An object containing the fields to update (e.g., { name, holidays }).
 */
export async function updateHolidayCalendar(
  tenantId: string,
  calendarId: string,
  updates: Partial<Pick<HolidayCalendar, 'name' | 'holidays'>>
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'holidayCalendars', calendarId);
  await updateDocSDK(docRef, updates);
}

/**
 * Deletes a Holiday Calendar document.
 * @param tenantId The tenant's ID.
 * @param calendarId The ID of the calendar to delete.
 */
export async function deleteHolidayCalendar(
  tenantId: string,
  calendarId: string
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'holidayCalendars', calendarId);
  await deleteDocSDK(docRef);
}

/**
 * Creates a Firestore query for all Holiday Calendars in a tenant,
 * ordered by name.
 * @param tenantId The tenant's ID.
 */
export function queryHolidayCalendars(
  tenantId: string
): Query<DocumentData, DocumentData> {
  // Safety cap: no company should have >100 holiday calendars.
  // limitSDK prevents an unbounded read that would grow with tenant data.
  return querySDK(
    collectionSDK(db, 'tenants', tenantId, 'holidayCalendars'),
    orderBySDK('name'),
    limitSDK(100)
  );
}

// --- Leave Policy Management ---

/**
 * Creates a new Leave Policy document for the tenant.
 * @param tenantId The tenant's ID.
 * @param policyData The data for the new policy.
 */
export async function createLeavePolicy(
  tenantId: string,
  policyData: Omit<LeavePolicy, 'id' | 'tenantId'>
): Promise<LeavePolicy> {
  const newDocRef = docSDK(collectionSDK(db, 'tenants', tenantId, 'leavePolicies'));

  const newPolicy: LeavePolicy = {
    id: newDocRef.id,
    tenantId,
    ...policyData,
  };

  await setDocSDK(newDocRef, newPolicy);
  return newPolicy;
}

/**
 * Updates an existing Leave Policy document.
 * @param tenantId The tenant's ID.
 * @param policyId The ID of the policy to update.
 * @param updates An object containing the fields to update.
 */
export async function updateLeavePolicy(
  tenantId: string,
  policyId: string,
  updates: Partial<Omit<LeavePolicy, 'id' | 'tenantId'>>
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'leavePolicies', policyId);
  await updateDocSDK(docRef, updates);
}

/**
 * Deletes a Leave Policy document.
 * @param tenantId The tenant's ID.
 * @param policyId The ID of the policy to delete.
 */
export async function deleteLeavePolicy(
  tenantId: string,
  policyId: string
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'leavePolicies', policyId);
  await deleteDocSDK(docRef);
}

/**
 * Creates a Firestore query for all Leave Policies in a tenant,
 * ordered by name.
 * @param tenantId The tenant's ID.
 */
export function queryLeavePolicies(
  tenantId: string
): Query<DocumentData, DocumentData> {
  // Safety cap: no company should have >100 leave policy types.
  // limitSDK prevents an unbounded read that would grow with tenant data.
  return querySDK(
    collectionSDK(db, 'tenants', tenantId, 'leavePolicies'),
    orderBySDK('name'),
    limitSDK(100)
  );
}

/**
 * Fetches the leave policies assigned to a specific employee.
 * Falls back to all available policies if none are assigned.
 * @param tenantId The tenant's ID.
 * @param employeeUid The employee's UID.
 * @returns An array of assigned LeavePolicy documents, or all policies if none assigned.
 */
export async function fetchAssignedLeavePolicies(
  tenantId: string,
  employeeUid: string
): Promise<LeavePolicy[]> {
  // 1. Fetch all policies for the tenant first (for fallback)
  const allPoliciesSnap = await getDocsSDK(queryLeavePolicies(tenantId));
  const allPolicies = allPoliciesSnap.docs.map((doc) => doc.data() as LeavePolicy);

  // 2. Get the employee document to read leavePolicyAssignments
  const employeeDoc = docSDK(db, 'tenants', tenantId, 'employees', employeeUid);
  const employeeSnap = await getDocSDK(employeeDoc);

  if (!employeeSnap.exists()) {
    console.warn(`Employee document not found for ${employeeUid}. Showing all policies as fallback.`);
    return allPolicies;
  }

  const employeeData = employeeSnap.data() as DocumentData;
  const leavePolicyAssignments = employeeData.leavePolicyAssignments as Record<string, boolean> | undefined;

  // 3. If no assignments exist, fall back to all policies
  if (!leavePolicyAssignments || Object.keys(leavePolicyAssignments).length === 0) {
    console.warn(`No leave policies assigned to ${employeeUid}. Showing all policies as fallback.`);
    return allPolicies;
  }

  // 4. Fetch all assigned leave policies by their IDs
  const assignedPolicyIds = Object.keys(leavePolicyAssignments).filter(
    (policyId) => leavePolicyAssignments[policyId] === true
  );

  if (assignedPolicyIds.length === 0) {
    console.warn(`No valid policy assignments for ${employeeUid}. Showing all policies as fallback.`);
    return allPolicies;
  }

  // Fetch each policy document
  const policyPromises = assignedPolicyIds.map(async (policyId) => {
    const policyDoc = docSDK(db, 'tenants', tenantId, 'leavePolicies', policyId);
    const policySnap = await getDocSDK(policyDoc);

    if (!policySnap.exists()) {
      return null;
    }

    return policySnap.data() as LeavePolicy;
  });

  const policies = await Promise.all(policyPromises);

  // Filter out null values (policies that don't exist) and sort by name
  const assignedPolicies = policies
    .filter((policy): policy is LeavePolicy => policy !== null)
    .sort((a, b) => a.name.localeCompare(b.name));

  // If filtering resulted in empty array but policies exist, return all as fallback
  if (assignedPolicies.length === 0 && allPolicies.length > 0) {
    console.warn(`Assigned policies not found for ${employeeUid}. Showing all policies as fallback.`);
    return allPolicies;
  }

  return assignedPolicies;
}

// --- Organization Management (Departments) ---

export async function createDepartment(
  tenantId: string,
  data: Omit<Department, 'id' | 'tenantId'>
): Promise<Department> {
  const newDocRef = docSDK(collectionSDK(db, 'tenants', tenantId, 'departments'));
  const newDept: Department = {
    id: newDocRef.id,
    tenantId,
    ...data,
  };
  await setDocSDK(newDocRef, newDept);
  return newDept;
}

export async function updateDepartment(
  tenantId: string,
  deptId: string,
  updates: Partial<Omit<Department, 'id' | 'tenantId'>>
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'departments', deptId);
  await updateDocSDK(docRef, updates);
}

/**
 * Delete a department
 *
 * Before deletion, checks for associated teams and employees.
 * - Teams in this department will have their departmentId set to null
 * - Employees in this department will have their departmentId set to null
 *
 * @param tenantId - Tenant ID
 * @param deptId - Department ID to delete
 * @throws Error if department contains teams or employees and reassignment fails
 */
export async function deleteDepartment(
  tenantId: string,
  deptId: string
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'departments', deptId);

  // Check for associated teams
  const teamsQuery = querySDK(
    collectionSDK(db, 'tenants', tenantId, 'teams'),
    whereSDK('departmentId', '==', deptId)
  );
  const teamsSnapshot = await getDocsSDK(teamsQuery);

  // Check for associated employees
  const employeesQuery = querySDK(
    collectionSDK(db, 'tenants', tenantId, 'employees'),
    whereSDK('departmentId', '==', deptId)
  );
  const employeesSnapshot = await getDocsSDK(employeesQuery);

  // Unassign teams from this department
  if (!teamsSnapshot.empty) {
    const teamUpdates = teamsSnapshot.docs.map(teamDoc => {
      const teamRef = docSDK(db, 'tenants', tenantId, 'teams', teamDoc.id);
      return updateDocSDK(teamRef, { departmentId: null });
    });
    await Promise.all(teamUpdates);
  }

  // Unassign employees from this department
  if (!employeesSnapshot.empty) {
    const employeeUpdates = employeesSnapshot.docs.map(empDoc => {
      const empRef = docSDK(db, 'tenants', tenantId, 'employees', empDoc.id);
      return updateDocSDK(empRef, { departmentId: null });
    });
    await Promise.all(employeeUpdates);
  }

  // Now safe to delete the department
  await deleteDocSDK(docRef);
}

export function queryDepartments(
  tenantId: string
): Query<DocumentData, DocumentData> {
  return querySDK(
    collectionSDK(db, 'tenants', tenantId, 'departments'),
    orderBySDK('name')
  );
}

// --- Organization Management (Teams) ---

export async function createTeam(
  tenantId: string,
  data: Omit<Team, 'id' | 'tenantId'>
): Promise<Team> {
  const newDocRef = docSDK(collectionSDK(db, 'tenants', tenantId, 'teams'));
  const newTeam: Team = {
    id: newDocRef.id,
    tenantId,
    ...data,
  };
  await setDocSDK(newDocRef, newTeam);
  return newTeam;
}

export async function updateTeam(
  tenantId: string,
  teamId: string,
  updates: Partial<Omit<Team, 'id' | 'tenantId'>>
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'teams', teamId);
  await updateDocSDK(docRef, updates);
}

/**
 * Delete a team
 *
 * Before deletion, checks for associated employees.
 * - Employees in this team will have their teamId set to null
 *
 * @param tenantId - Tenant ID
 * @param teamId - Team ID to delete
 * @throws Error if team contains employees and reassignment fails
 */
export async function deleteTeam(
  tenantId: string,
  teamId: string
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'teams', teamId);

  // Check for associated employees
  const employeesQuery = querySDK(
    collectionSDK(db, 'tenants', tenantId, 'employees'),
    whereSDK('teamId', '==', teamId)
  );
  const employeesSnapshot = await getDocsSDK(employeesQuery);

  // Unassign employees from this team
  if (!employeesSnapshot.empty) {
    const employeeUpdates = employeesSnapshot.docs.map(empDoc => {
      const empRef = docSDK(db, 'tenants', tenantId, 'employees', empDoc.id);
      return updateDocSDK(empRef, { teamId: null });
    });
    await Promise.all(employeeUpdates);
  }

  // Now safe to delete the team
  await deleteDocSDK(docRef);
}

export function queryTeams(
  tenantId: string
): Query<DocumentData, DocumentData> {
  return querySDK(
    collectionSDK(db, 'tenants', tenantId, 'teams'),
    orderBySDK('name')
  );
}

// --- Organization Management (Job Titles) ---

export async function createJobTitle(
  tenantId: string,
  data: Omit<JobTitle, 'id' | 'tenantId'>
): Promise<JobTitle> {
  const newDocRef = docSDK(collectionSDK(db, 'tenants', tenantId, 'jobTitles'));
  const newJobTitle: JobTitle = {
    id: newDocRef.id,
    tenantId,
    ...data,
  };
  await setDocSDK(newDocRef, newJobTitle);
  return newJobTitle;
}

export async function updateJobTitle(
  tenantId: string,
  jobTitleId: string,
  updates: Partial<Omit<JobTitle, 'id' | 'tenantId'>>
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'jobTitles', jobTitleId);
  await updateDocSDK(docRef, updates);
}

/**
 * Delete a job title
 *
 * Before deletion, checks for associated employees.
 * - Employees with this jobTitleId will have their jobTitleId set to null
 *
 * @param tenantId - Tenant ID
 * @param jobTitleId - Job Title ID to delete
 */
export async function deleteJobTitle(
  tenantId: string,
  jobTitleId: string
): Promise<void> {
  const docRef = docSDK(db, 'tenants', tenantId, 'jobTitles', jobTitleId);

  // Check for associated employees
  const employeesQuery = querySDK(
    collectionSDK(db, 'tenants', tenantId, 'employees'),
    whereSDK('jobTitleId', '==', jobTitleId)
  );
  const employeesSnapshot = await getDocsSDK(employeesQuery);

  // Unassign employees from this job title
  if (!employeesSnapshot.empty) {
    const employeeUpdates = employeesSnapshot.docs.map(empDoc => {
      const empRef = docSDK(db, 'tenants', tenantId, 'employees', empDoc.id);
      return updateDocSDK(empRef, { jobTitleId: null });
    });
    await Promise.all(employeeUpdates);
  }

  // Now safe to delete the job title
  await deleteDocSDK(docRef);
}

export function queryJobTitles(
  tenantId: string
): Query<DocumentData, DocumentData> {
  return querySDK(
    collectionSDK(db, 'tenants', tenantId, 'jobTitles'),
    orderBySDK('name')
  );
}

/**
 * Query expense merchants for a tenant
 * Returns active merchants sorted by displayOrder, then name
 */
export function queryExpenseMerchants(
  tenantId: string,
  includeInactive = false
): Query<DocumentData, DocumentData> {
  const col = collectionSDK(db, 'tenants', tenantId, 'expenseMerchants');
  if (includeInactive) {
    // All merchants, sorted by displayOrder then name
    return querySDK(col, orderBySDK('displayOrder'), orderBySDK('name'));
  }
  // Only active merchants, sorted by displayOrder then name
  return querySDK(col, whereSDK('active', '==', true), orderBySDK('displayOrder'), orderBySDK('name'));
}
